import { SiMicrosoftexcel } from '@icons-pack/react-simple-icons';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setNotifications } from '../../../../../../redux/features/app/appSlice';
import { cloneAndProcessTable, copyTableContent } from '../../../../../utils/CopyUtils';
import { NOTIFICATION_TEMPLATES } from '../../../Notifications/NotificationConstants';
import { Table, TableRowProps } from '../../Shared/Table';

const generateTableElementId = () => {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    );
};

const extractHeadings = (children) => {
    if (!children?.[0]?.props?.children?.props?.children) {
        console.error('Invalid structure for headings extraction');
        return [];
    }

    return children[0].props.children.props.children.map((child) => child?.props?.children || null).filter(Boolean); // Filter out any null values
};

const extractRows = (children): TableRowProps[] => {
    if (!children || !children[1] || !children[1].props || !children[1].props.children) {
        return [];
    }

    return Object.values(children[1].props.children)
        .map((child: any) => {
            if (!child || !child.props) {
                return null;
            }

            const row: TableRowProps = {
                id: child.key,
                checkbox: child.props.checkbox,
                checked: child.props.checked,
                type: child.props.type,
            };

            if (child.props.children) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                Object.values(child.props.children).forEach((c: any) => {
                    if (c && c.props && c.props.children) {
                        row[c.key] = c.props.children;
                    } else {
                        row[c.key] = '';
                        //console.warn('Invalid child structure in row children');
                    }
                });
            }

            return row;
        })
        .filter((row): row is TableRowProps => row !== null); // Filter out any null values
};

const TableRenderer = (props) => {
    const dispatch = useDispatch();
    const [messageCopied, setMessageCopied] = useState(false);
    const [messageExported, setMessageExported] = useState(false);
    const tableElementId = useMemo(generateTableElementId, []);
    const headings = useMemo(() => extractHeadings(props.children), [props.children]);
    const rows = useMemo(() => extractRows(props.children), [props.children]);

    const handleExportToExcelClick = async () => {
        try {
            const module = await import('excellentexport');

            const tableEl = document.querySelector(`#table-${tableElementId} table`) as HTMLTableElement;
            const anchorEl = document.querySelector(`#export-table-${tableElementId}`) as HTMLAnchorElement;

            if (!tableEl || !anchorEl) {
                console.error('Table or anchor element not found');
                return;
            }

            setMessageExported(true);
            dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.EXPORT_PROMPT_TABLE }]));

            const processedTable = cloneAndProcessTable(tableEl);
            module.default.excel(anchorEl, processedTable, 'Sheet Name');

            anchorEl.click();
        } catch (error) {
            console.error('Error during export:', error);
        }
    };

    const handleCopyClick = () => {
        void copyTableContent(`table-${tableElementId}`, setMessageCopied);
        dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_PROMPT_TABLE }]));
    };

    return (
        <div id={`table-${tableElementId}`} className="mt-4 relative group">
            <Table
                data={{
                    headings: headings,
                    rows: rows,
                    hidden: ['type', 'checkbox', 'checked', 'id', 'siteId'],
                }}
            />

            <div className="mb-2 flex w-full flex-row justify-end gap-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <a download="data.xls" id={`export-table-${tableElementId}`} href="#" className="hidden" />
                <button
                    title={messageExported ? 'Exported' : 'Export table to Excel'}
                    className="dark:hover:text-slate-50"
                    type="button"
                    onClick={handleExportToExcelClick}>
                    <SiMicrosoftexcel
                        size={14}
                        className="w-4 h-4"
                        onPointerEnterCapture={null}
                        onPointerLeaveCapture={null}
                    />
                </button>
                <button
                    title={messageCopied ? 'Copied' : 'Copy table'}
                    className="dark:hover:text-slate-50"
                    type="button"
                    onClick={handleCopyClick}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke={messageCopied ? 'currentColor' : 'currentColor'}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={
                            messageCopied ? 'h-5 w-5 stroke-gray-300 dark:stroke-gray-500' : ' h-5 w-5 stroke-current'
                        }>
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                        <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default TableRenderer;
